import { tv, type VariantProps } from 'tailwind-variants';
import { ArticleCardTimed } from '@/app_components/ArticleSections/ArticleCards/ArticleCardTimed';
import { StrikethroughHeading } from '@/app_components/ArticleSections/Heading';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import { ContentPlacement } from '@/app_lib/types/analytics';
import type { EventData } from '@/app_lib/types/Gtm';
import { cn } from '@/app_lib/utils/utils';

const articleSectionRelated = tv({
  defaultVariants: {
    layout: 'grid-3col',
  },
  slots: {
    base: 'flex flex-col layout-gap-md w-full',
    grid: 'latest-popular-content',
  },
  variants: {
    layout: {
      'grid-2col-mobile-2col-desktop': {
        grid: 'grid grid-cols-2 gap-xl md:layout-grid-gap md:grid-cols-2',
      },
      'grid-3col': {
        grid: 'flex flex-col gap-xl md:layout-grid-gap md:grid md:grid-cols-3',
      },
      'grid-4col': {
        grid: 'grid grid-cols-2 gap-xl md:layout-grid-gap md:grid md:grid-cols-2 lg:grid lg:grid-cols-4',
      },
      list: {
        grid: 'flex flex-col gap-xl',
      },
    },
  },
});

type SectionVariants = VariantProps<typeof articleSectionRelated>;

interface ArticleSectionRelatedProps extends SectionVariants {
  articles: ArticlePreview[];
  className?: string;
  /** @experiment recirc_layout_test — render cards as direct children for subgrid alignment */
  flat?: boolean;
  heading: string;
  /** @experiment recirc_layout_test — className applied to heading wrapper for grid placement */
  headingClassName?: string;
  maxArticles?: number;
  priority?: boolean;
  trackingData?: EventData;
}

const defaultTrackingData: EventData = {
  // biome-ignore lint/style/useNamingConvention: gtm data
  contentPlacementCD: 'latest - bottom',
  eventAction: 'latest module click',
  eventCategory: 'navigation',
};

export const ArticleSectionRelated = ({
  articles,
  className,
  flat,
  heading,
  headingClassName,
  layout,
  maxArticles,
  priority,
  trackingData = defaultTrackingData,
}: ArticleSectionRelatedProps) => {

  if (articles.length === 0) {
    return null;
  }
  const displayArticles = maxArticles ? articles.slice(0, maxArticles) : articles;
  const recirculationArray = articles.map((article) => article.title);
  const { base, grid } = articleSectionRelated({ layout });

  const cards = displayArticles.map((article, index) => (
    <div key={article.url}>
      <ArticleCardTimed
        article={article}
        index={index + 1}
        priority={priority}
        trackingData={trackingData}
        variant={layout === 'list' ? 'small' : 'timed'}
      />
    </div>
  ));


  return (
    <div
      className={cn(base(), className)}
      data-cy={ContentPlacement.ArticleTitle}
      data-recirculation-array={recirculationArray}
      data-recirculation-name={heading}
    >
      <StrikethroughHeading
        className={headingClassName}
        id='latest-popular-module'
      >
        {heading}
      </StrikethroughHeading>
      <div
        className={cn(grid(), flat && 'md:contents')}
        data-cy={ContentPlacement.PostList}
      >
        {cards}
      </div>
    </div>
  );
};
